import { useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useCurrentZone } from '@/hooks/useCurrentZone';
import { getZoneFirstData, zoneFirstDataKey, type DataSection } from '@/api/features';

/** Пусто за выбранный период — одинаково во всём продукте (Алексей 16.09).
 *
 *  Просьба была именно обобщить: «нет данных за выбранный период» плюс кнопка
 *  с РЕКОМЕНДОВАННЫМ периодом, потому что в разных разделах разумный запасной
 *  период разный — по отзывам это три месяца (их мало), по речи вчерашний день.
 *  Кнопка сама переписывает ?from/?to, поэтому вкладке не нужно прокидывать
 *  сеттер периода.
 */
export function NoDataForPeriod({ what, suggestion, section, className }: {
  /** Родительный падеж: «отзывов», «разговоров», «данных». */
  what: string;
  suggestion?: { label: string; from: string; to: string };
  /** Раздел для второй кнопки «перейти к данным»: прыжок к последним данным
   *  раздела той же длины, что выбранный период (Алексей 16.09, п. 50). */
  section?: DataSection;
  className?: string;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { zoneId } = useCurrentZone();
  const { data: span } = useSWR(
    section ? zoneFirstDataKey(zoneId) : null,
    () => getZoneFirstData(String(zoneId)),
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );
  const last = section ? span?.[section]?.last ?? null : null;
  const first = section ? span?.[section]?.first ?? null : null;
  // Период той же длины, что выбран, заканчивающийся последними данными;
  // если выбранный уже их накрывает — прыгать некуда.
  const jump = (() => {
    if (!last) return null;
    const from = searchParams.get('from') ?? isoDay(0);
    const to = searchParams.get('to') ?? isoDay(0);
    if (from <= last && last <= to) return null;
    const len = Math.max(0, Math.round((Date.parse(to) - Date.parse(from)) / 86400000));
    const d = new Date(`${last}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() - len);
    let jFrom = d.toISOString().slice(0, 10);
    if (first && jFrom < first) jFrom = first;
    return { from: jFrom, to: last };
  })();
  const go = (f: string, t: string) =>
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('from', f);
      p.set('to', t);
      return p;
    }, { replace: true });
  return (
    <div className={cn('flex flex-col items-center gap-3 py-10 text-center', className)}>
      <p className="text-sm text-muted-foreground">За выбранный период {what} нет</p>
      <div className="flex flex-wrap items-center justify-center gap-2">
        {suggestion && (
          <Button variant="outline" size="sm" onClick={() => go(suggestion.from, suggestion.to)}>
            {suggestion.label}
          </Button>
        )}
        {jump && (
          <Button variant="ghost" size="sm" onClick={() => go(jump.from, jump.to)} title={`${jump.from} — ${jump.to}`}>
            Перейти к данным
          </Button>
        )}
      </div>
    </div>
  );
}

/** Сегодня и сдвиги от него — в одном месте, чтобы вкладки не считали даты по-разному. */
export const isoDay = (shiftDays = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + shiftDays);
  return d.toISOString().slice(0, 10);
};
